import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Divider,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Instagram as InstagramIcon,
  Message as DMIcon,
  Favorite as LikeIcon,
  ChatBubble as CommentIcon,
  AutoStories as StoryIcon,
  Add as AddIcon,
  CheckCircle as CheckIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  AutoMode as AutoIcon,
  Public as GlobeIcon,
  LocationOn as LocationIcon,
  Visibility as VisitIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { leadApi, activityApi, trackingApi } from '../services/api';
import { useApp } from '../context/AppContext';
import { LoadingSpinner, StageChip, NicheChip, ConfirmDialog } from '../components/common';
import { TrackingDocs } from '../components/settings';
import {
  formatInstagramHandle,
  formatDate,
  formatRelativeDate,
  formatNumber,
  getDaysInStage,
  getInstagramProfileUrl,
  getInstagramDmUrl,
  getActivityIcon,
  getActivityTypeName,
  getStageDisplayName,
} from '../utils/helpers';
import { stageColors } from '../theme';

const stages = [
  'new', 'warming', 'dm_sent', 'replied', 'call_scheduled',
  'call_completed', 'proposal_sent', 'negotiating', 'won', 'lost', 'not_interested'
];

const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSnackbar, fetchDashboardData } = useApp();

  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [activityDialog, setActivityDialog] = useState(false);
  const [convertDialog, setConvertDialog] = useState(false);
  const [lostDialog, setLostDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [followupDialog, setFollowupDialog] = useState(false);
  const [trackingDocsDialog, setTrackingDocsDialog] = useState(false);

  // Follow-up form
  const [newFollowupDate, setNewFollowupDate] = useState(null);
  const [newFollowupNotes, setNewFollowupNotes] = useState('');

  // Activity form
  const [activityType, setActivityType] = useState('note');
  const [activityDescription, setActivityDescription] = useState('');

  // Convert form
  const [convertData, setConvertData] = useState({
    monthlyRate: 40,
    billingStartDate: new Date(),
  });

  // Lost form
  const [lostReason, setLostReason] = useState('');

  const fetchLead = async () => {
    try {
      const [leadRes, activitiesRes, visitsRes] = await Promise.all([
        leadApi.getById(id),
        leadApi.getActivities(id),
        trackingApi.getLeadVisits(id).catch(() => ({ data: { visits: [] } })),
      ]);
      setLead(leadRes.data);
      setActivities(activitiesRes.data.activities || []);
      setVisits(visitsRes.data.visits || []);
    } catch (error) {
      showSnackbar('Failed to load lead', 'error');
      navigate('/leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleStageChange = async (newStage) => {
    try {
      await leadApi.update(id, { stage: newStage });
      setLead({ ...lead, stage: newStage });
      showSnackbar('Stage updated', 'success');
      fetchLead();
      fetchDashboardData();
    } catch (error) {
      showSnackbar('Failed to update stage', 'error');
    }
  };

  const handleWarmup = async (type) => {
    try {
      await leadApi.incrementWarmup(id, type);
      showSnackbar(`${type} logged!`, 'success');
      fetchLead();
    } catch (error) {
      showSnackbar('Failed to log activity', 'error');
    }
  };

  const handleAddActivity = async () => {
    try {
      await activityApi.create({
        lead: id,
        type: activityType,
        description: activityDescription,
      });
      showSnackbar('Activity logged', 'success');
      setActivityDialog(false);
      setActivityType('note');
      setActivityDescription('');
      fetchLead();
    } catch (error) {
      showSnackbar('Failed to add activity', 'error');
    }
  };

  const handleConvert = async () => {
    try {
      await leadApi.convertToClient(id, convertData);
      showSnackbar('Lead converted to client!', 'success');
      setConvertDialog(false);
      fetchDashboardData();
      navigate('/clients');
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to convert lead', 'error');
    }
  };

  const handleMarkLost = async () => {
    try {
      await leadApi.update(id, {
        stage: 'lost',
        lostReason,
        lostDate: new Date(),
      });
      showSnackbar('Lead marked as lost', 'success');
      setLostDialog(false);
      fetchLead();
      fetchDashboardData();
    } catch (error) {
      showSnackbar('Failed to update lead', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await leadApi.delete(id);
      showSnackbar('Lead deleted', 'success');
      navigate('/leads');
      fetchDashboardData();
    } catch (error) {
      showSnackbar('Failed to delete lead', 'error');
    }
  };

  const handleFollowupUpdate = async (date) => {
    try {
      await leadApi.update(id, { nextFollowupDate: date });
      setLead({ ...lead, nextFollowupDate: date });
      showSnackbar('Follow-up date updated', 'success');
    } catch (error) {
      showSnackbar('Failed to update follow-up date', 'error');
    }
  };

  const handleAddFollowup = async () => {
    if (!newFollowupDate) {
      showSnackbar('Please select a date', 'error');
      return;
    }
    try {
      const response = await leadApi.addFollowup(id, {
        date: newFollowupDate,
        notes: newFollowupNotes,
      });
      setLead(response.data);
      setFollowupDialog(false);
      setNewFollowupDate(null);
      setNewFollowupNotes('');
      showSnackbar('Follow-up scheduled', 'success');
    } catch (error) {
      showSnackbar('Failed to add follow-up', 'error');
    }
  };

  const handleCompleteFollowup = async (followupId) => {
    try {
      const response = await leadApi.completeFollowup(id, followupId);
      setLead(response.data);
      showSnackbar('Follow-up marked complete', 'success');
    } catch (error) {
      showSnackbar('Failed to complete follow-up', 'error');
    }
  };

  const handleDeleteFollowup = async (followupId) => {
    try {
      const response = await leadApi.deleteFollowup(id, followupId);
      setLead(response.data);
      showSnackbar('Follow-up deleted', 'success');
    } catch (error) {
      showSnackbar('Failed to delete follow-up', 'error');
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading lead..." />;
  }

  if (!lead) {
    return null;
  }

  const warmupProgress = ((lead.likesGiven || 0) + (lead.commentsGiven || 0) + (lead.storyReplies || 0)) / 5 * 100;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/leads')}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>
            {formatInstagramHandle(lead.instagramHandle)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {lead.businessName || 'No business name'} • {lead.city}, {lead.stateProvince}
          </Typography>
        </Box>
        <StageChip stage={lead.stage} size="medium" />
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/leads/${id}/edit`)}
        >
          Edit
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>
          {/* Quick Actions */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<InstagramIcon />}
                  onClick={() => window.open(getInstagramProfileUrl(lead.instagramHandle), '_blank')}
                >
                  View Profile
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DMIcon />}
                  onClick={() => window.open(getInstagramDmUrl(lead.instagramHandle), '_blank')}
                >
                  Send DM
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<LikeIcon />}
                  onClick={() => handleWarmup('like')}
                >
                  Log Like ({lead.likesGiven || 0})
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CommentIcon />}
                  onClick={() => handleWarmup('comment')}
                >
                  Log Comment ({lead.commentsGiven || 0})
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<StoryIcon />}
                  onClick={() => handleWarmup('story_reply')}
                >
                  Log Story Reply ({lead.storyReplies || 0})
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setActivityDialog(true)}
                >
                  Add Activity
                </Button>
              </Box>

              {/* Warmup Progress */}
              {(lead.stage === 'new' || lead.stage === 'warming') && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Warm-up Progress
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {lead.likesGiven || 0} likes, {lead.commentsGiven || 0} comments, {lead.storyReplies || 0} stories
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(warmupProgress, 100)}
                    color="warning"
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Pitch Site Visits */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <VisitIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Pitch Site Visits
                </Typography>
                {visits.length > 0 && (
                  <Chip label={visits.length} size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem' }} />
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  size="small"
                  startIcon={<CodeIcon sx={{ fontSize: 14 }} />}
                  onClick={() => setTrackingDocsDialog(true)}
                  sx={{ fontSize: '0.7rem' }}
                >
                  Setup
                </Button>
              </Box>

              {visits.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No visits tracked yet
                </Typography>
              ) : (
                <Box sx={{ maxHeight: 250, overflowY: 'auto' }}>
                  <List dense disablePadding>
                    {visits.slice(0, 5).map((visit) => (
                      <ListItem key={visit._id} sx={{ px: 0, py: 0.5 }}>
                        <ListItemAvatar sx={{ minWidth: 36 }}>
                          <Avatar sx={{ width: 28, height: 28, bgcolor: visit.isFirstVisit ? 'success.light' : 'grey.200' }}>
                            <GlobeIcon sx={{ fontSize: 16, color: visit.isFirstVisit ? 'success.dark' : 'grey.600' }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: visit.isFirstVisit ? 600 : 400,
                                  fontSize: '0.75rem',
                                  wordBreak: 'break-all'
                                }}
                              >
                                {visit.refererUrl || visit.domain}
                              </Typography>
                              {visit.isFirstVisit && (
                                <Chip label="First" size="small" color="success" sx={{ height: 16, fontSize: '0.6rem', flexShrink: 0 }} />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                              <LocationIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {visit.location?.city || 'Unknown'}, {visit.location?.country || 'Unknown'}
                              </Typography>
                              <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>
                                {formatRelativeDate(visit.createdAt)}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
              {visits.length > 5 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                  +{visits.length - 5} more visits
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Stage Pipeline */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Move Stage
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {stages.map((stage) => (
                  <Chip
                    key={stage}
                    label={getStageDisplayName(stage)}
                    onClick={() => stage === 'won' ? setConvertDialog(true) : stage === 'lost' ? setLostDialog(true) : handleStageChange(stage)}
                    sx={{
                      bgcolor: lead.stage === stage ? stageColors[stage] : `${stageColors[stage]}20`,
                      color: lead.stage === stage ? 'white' : stageColors[stage],
                      fontWeight: 600,
                      '&:hover': { bgcolor: stageColors[stage], color: 'white' },
                    }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* Activity Timeline */}
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Activity Timeline
              </Typography>
              {activities.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No activities yet
                </Typography>
              ) : (
                <List disablePadding>
                  {activities.map((activity) => (
                    <ListItem key={activity._id} sx={{ px: 0 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'grey.100' }}>
                          {getActivityIcon(activity.type)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={getActivityTypeName(activity.type)}
                        secondary={
                          <>
                            {activity.description && <span>{activity.description} • </span>}
                            {formatRelativeDate(activity.createdAt)}
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={4}>
          {/* Lead Info */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Lead Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Owner Name</Typography>
                  <Typography variant="body2">{lead.ownerName || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography variant="body2">{lead.email || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Phone</Typography>
                  <Typography variant="body2">{lead.phone || '-'}</Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary">Niche</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <NicheChip niche={lead.niche} />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Followers</Typography>
                  <Typography variant="body2">{formatNumber(lead.followerCount)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Has Website</Typography>
                  <Typography variant="body2">{lead.hasWebsite ? 'Yes' : 'No'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Current Booking Method</Typography>
                  <Typography variant="body2">{lead.currentBookingMethod || '-'}</Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary">Source</Typography>
                  <Typography variant="body2">{lead.source || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Days in Stage</Typography>
                  <Typography variant="body2">{getDaysInStage(lead.stageUpdatedAt)} days</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Added</Typography>
                  <Typography variant="body2">{formatDate(lead.createdAt)}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Follow-ups */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Follow-ups
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                  onClick={() => setFollowupDialog(true)}
                  sx={{ fontSize: '0.75rem' }}
                >
                  Add
                </Button>
              </Box>

              {/* Follow-up List */}
              {lead.followups && lead.followups.length > 0 ? (
                <List dense disablePadding>
                  {lead.followups
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .map((followup) => {
                      const isOverdue = !followup.completed && new Date(followup.date) < new Date();
                      const isToday = !followup.completed &&
                        new Date(followup.date).toDateString() === new Date().toDateString();

                      return (
                        <ListItem
                          key={followup._id}
                          sx={{
                            px: 0,
                            py: 0.5,
                            opacity: followup.completed ? 0.5 : 1,
                            bgcolor: isOverdue ? 'error.light' : isToday ? 'warning.light' : 'transparent',
                            borderRadius: 1,
                            mb: 0.5,
                          }}
                          secondaryAction={
                            !followup.completed && (
                              <Box>
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleCompleteFollowup(followup._id)}
                                >
                                  <CheckIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteFollowup(followup._id)}
                                >
                                  <DeleteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Box>
                            )
                          }
                        >
                          <ListItemAvatar sx={{ minWidth: 32 }}>
                            {followup.type === 'auto_stage' ? (
                              <AutoIcon sx={{ fontSize: 18, color: 'info.main' }} />
                            ) : (
                              <ScheduleIcon sx={{ fontSize: 18, color: followup.completed ? 'success.main' : 'primary.main' }} />
                            )}
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body2"
                                sx={{
                                  textDecoration: followup.completed ? 'line-through' : 'none',
                                  fontWeight: isToday || isOverdue ? 600 : 400,
                                }}
                              >
                                {formatDate(followup.date)}
                                {followup.type === 'auto_stage' && (
                                  <Chip
                                    label="Auto"
                                    size="small"
                                    color="info"
                                    sx={{ ml: 1, height: 16, fontSize: '0.65rem' }}
                                  />
                                )}
                                {followup.type === 'bulk' && (
                                  <Chip
                                    label="Bulk"
                                    size="small"
                                    color="secondary"
                                    sx={{ ml: 1, height: 16, fontSize: '0.65rem' }}
                                  />
                                )}
                              </Typography>
                            }
                            secondary={followup.notes}
                            secondaryTypographyProps={{ sx: { fontSize: '0.75rem' } }}
                          />
                        </ListItem>
                      );
                    })}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No follow-ups scheduled
                </Typography>
              )}

              {/* Legacy single follow-up display */}
              {(!lead.followups || lead.followups.length === 0) && lead.nextFollowupDate && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Next Follow-up (Legacy)
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatDate(lead.nextFollowupDate)}
                  </Typography>
                  {lead.followupNotes && (
                    <Typography variant="caption" color="text.secondary">
                      {lead.followupNotes}
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Notes
              </Typography>
              <Typography variant="body2">
                {lead.notes || 'No notes'}
              </Typography>
            </CardContent>
          </Card>

          {/* Danger Zone */}
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="error" gutterBottom>
                Danger Zone
              </Typography>
              <Button
                variant="outlined"
                color="error"
                fullWidth
                onClick={() => setDeleteDialog(true)}
              >
                Delete Lead
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add Activity Dialog */}
      <Dialog open={activityDialog} onClose={() => setActivityDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Activity</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              select
              label="Activity Type"
              value={activityType}
              onChange={(e) => setActivityType(e.target.value)}
              fullWidth
            >
              <MenuItem value="dm_sent">DM Sent</MenuItem>
              <MenuItem value="dm_received">DM Received</MenuItem>
              <MenuItem value="call_scheduled">Call Scheduled</MenuItem>
              <MenuItem value="call_completed">Call Completed</MenuItem>
              <MenuItem value="proposal_sent">Proposal Sent</MenuItem>
              <MenuItem value="follow_up">Follow Up</MenuItem>
              <MenuItem value="note">Note</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
            <TextField
              label="Description"
              value={activityDescription}
              onChange={(e) => setActivityDescription(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivityDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddActivity}>Add Activity</Button>
        </DialogActions>
      </Dialog>

      {/* Convert to Client Dialog */}
      <Dialog open={convertDialog} onClose={() => setConvertDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Convert to Client</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Monthly Rate"
              type="number"
              value={convertData.monthlyRate}
              onChange={(e) => setConvertData({ ...convertData, monthlyRate: parseFloat(e.target.value) })}
              fullWidth
              InputProps={{ startAdornment: '$' }}
            />
            <DatePicker
              label="Billing Start Date"
              value={convertData.billingStartDate}
              onChange={(date) => setConvertData({ ...convertData, billingStartDate: date })}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertDialog(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleConvert}>Convert to Client</Button>
        </DialogActions>
      </Dialog>

      {/* Mark Lost Dialog */}
      <Dialog open={lostDialog} onClose={() => setLostDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark as Lost</DialogTitle>
        <DialogContent>
          <TextField
            label="Reason"
            value={lostReason}
            onChange={(e) => setLostReason(e.target.value)}
            multiline
            rows={3}
            fullWidth
            sx={{ mt: 1 }}
            placeholder="Why was this lead lost?"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLostDialog(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleMarkLost}>Mark as Lost</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialog}
        title="Delete Lead"
        message={`Are you sure you want to delete ${formatInstagramHandle(lead.instagramHandle)}? This action cannot be undone.`}
        confirmText="Delete"
        confirmColor="error"
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialog(false)}
      />

      {/* Add Follow-up Dialog */}
      <Dialog open={followupDialog} onClose={() => setFollowupDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Schedule Follow-up</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <DatePicker
              label="Follow-up Date"
              value={newFollowupDate}
              onChange={(date) => setNewFollowupDate(date)}
              minDate={new Date()}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                }
              }}
            />
            <TextField
              fullWidth
              label="Notes (optional)"
              value={newFollowupNotes}
              onChange={(e) => setNewFollowupNotes(e.target.value)}
              multiline
              rows={2}
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFollowupDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddFollowup}>
            Schedule
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tracking Docs Dialog */}
      <TrackingDocs
        open={trackingDocsDialog}
        onClose={() => setTrackingDocsDialog(false)}
        leadId={id}
      />
    </Box>
  );
};

export default LeadDetail;
